import { createMemo, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { useCardMove } from '../../lib/card-move';
import { panelTop } from '../../lib/move-drag';
import { DropPanel } from './DropPanel';
import { MoveIcon } from './MoveIcon';

// A card as a drop target (#6771, prototype dropHTML `:1943-1953`, the `.dz-drop` rules
// `:928-940`): while work from ANOTHER machine is dragged, a dashed overlay and a pill, "Drop to
// move here" or "Can’t take it" ("Checking…" until the server's preview answers). The hovered
// card lifts 3 px and opens its panel (`DropPanel`), above the pointer when there is room.
//
// Mounted only where the card's move handle is (an admin on an own-org machine), and shown only
// for a machine the preview names: a member, a viewer or another org's machine is never a drop
// target. Decorative to assistive tech: the drag announces its own progress, and Move to is the
// keyboard's way.

export function DropTarget(): JSX.Element {
  const m = useCardMove();
  const [panelH, setPanelH] = createSignal(180);
  let el: HTMLDivElement | undefined;
  const id = (): string | null => m?.box().id ?? null;
  /** This card's answer while work from another machine is dragged; null otherwise. */
  const view = createMemo(() => {
    const s = m?.page.dragging() ?? null;
    const box = id();
    if (m === null || s === null || box === null || s.sourceId === box) return null;
    const v = m.page.targetView(box);
    return v === null ? null : { subject: s, view: v };
  });
  const over = (): number | null => {
    const o = m?.page.over() ?? null;
    return o !== null && o.id === id() ? o.y : null;
  };
  /** The hovered card's panel, once the preview has answered for it; re-read on every change. */
  const panel = createMemo(() => {
    const v = view();
    const session = m?.page.session();
    if (m === null || over() === null || v === null || v.view.kind !== 'answer') return null;
    if (session?.preview.status !== 'ready') return null;
    return {
      subject: v.subject,
      answer: v.view,
      preview: session.preview.preview,
      target: m.box(),
      source: m.page.boxOf(v.subject.sourceId),
    };
  });
  const refusing = (): boolean => {
    const v = view()?.view;
    return v?.kind === 'answer' && v.why !== null;
  };
  const top = (): number => {
    const y = over();
    return y === null ? 72 : panelTop(y, el?.offsetHeight ?? 0, panelH());
  };
  return (
    <Show when={view()}>
      {(v) => (
        <div
          ref={el}
          class="move-drop"
          classList={{
            'move-drop--live': !refusing() && v().view.kind === 'answer',
            'move-drop--no': refusing() || v().view.kind === 'checking',
            'move-drop--over': over() !== null,
          }}
          data-move-drop=""
          aria-hidden="true"
        >
          <span class="move-drop__pill">
            <MoveIcon glyph={refusing() ? 'block' : 'down'} />
            {v().view.kind === 'checking'
              ? t('fleet.move.checking')
              : refusing()
                ? t('fleet.move.drop_no')
                : t('fleet.move.drop_ok')}
          </span>
          <Show when={panel()}>
            {(p) => (
              <DropPanel
                onMeasure={(node) =>
                  queueMicrotask(() => {
                    // Measured after mount; a panel already gone sets nothing.
                    if (node.isConnected) setPanelH(node.offsetHeight || 180);
                  })
                }
                subject={p().subject}
                target={p().target}
                source={p().source}
                answer={p().answer.target}
                preview={p().preview}
                why={p().answer.why}
                top={top()}
              />
            )}
          </Show>
        </div>
      )}
    </Show>
  );
}
